import { colors } from '@/theme'
import { Edit } from '@mui/icons-material'
import { Box, Button, IconButton, Paper, TextField, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'

interface NotesSectionProps {
  id?: string
  title: string
  notes?: string
  onSave?: (notes: string) => void
  actionButton?: {
    label: string
    onClick: () => void
  }
  readOnly?: boolean
}

export default function NotesSection({
  id,
  title,
  notes: initialNotes,
  onSave,
  actionButton,
  readOnly
}: NotesSectionProps) {
  const [isEditMode, setIsEditMode] = useState(false)
  const [notes, setNotes] = useState(initialNotes || '')
  const prevPropNotesRef = useRef(initialNotes || '')

  useEffect(() => {
    // Only sync from props when the prop value actually changed externally (not from our local save)
    const currentPropNotes = initialNotes || ''
    if (currentPropNotes !== prevPropNotesRef.current && !isEditMode) {
      setNotes(currentPropNotes)
      prevPropNotesRef.current = currentPropNotes
    }
  }, [initialNotes, isEditMode])

  const handleEdit = () => {
    setIsEditMode(true)
  }

  const handleCancel = () => {
    setIsEditMode(false)
    setNotes(initialNotes || '')
  }

  const handleSave = () => {
    onSave?.(notes)
    setIsEditMode(false)
  }

  return (
    <Box id={id}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: `1px solid ${colors.border.default}`,
          bgcolor: colors.background.paper,
          p: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              sx={{
                color: colors.text.dark,
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: 1.5
              }}
            >
              {title}
            </Typography>
            {actionButton && !isEditMode && !readOnly && (
              <Button variant='secondary' size='small' onClick={actionButton.onClick}>
                {actionButton.label}
              </Button>
            )}
          </Box>
          {!isEditMode && !readOnly && (
            <IconButton size='small' onClick={handleEdit}>
              <Edit sx={{ fontSize: '16px', color: colors.brand.primary }} />
            </IconButton>
          )}
        </Box>
        {isEditMode ? (
          <Box>
            <TextField
              multiline
              rows={4}
              fullWidth
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder='Add Notes'
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  fontSize: '14px',
                  '& fieldset': {
                    borderColor: colors.border.default
                  },
                  '&:hover fieldset': {
                    borderColor: colors.border.hover
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.brand.primary
                  }
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant='ghost' onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant='primary' onClick={handleSave}>
                Save information
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography
            sx={{ color: notes ? colors.text.secondary : colors.text.muted, fontSize: '14px', lineHeight: 1.6 }}
          >
            {notes || 'Add Notes'}
          </Typography>
        )}
      </Paper>
    </Box>
  )
}
